import React, { useState } from 'react'
import { EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { toast } from 'react-toastify'
import {
  Image,
  AlbumType,
  isImageInAlbum,
  removeFromAlbum,
} from './types/models'
import { FaFolderOpen, FaInfoCircle } from 'react-icons/fa'
import ImageWithApiKey from '../ImageWithApiKey'
import { X_API_KEY } from '../../../services/api'

interface GalleryAlbumsProps {
  images: Image[]
  selectedImages: string[]
  onImageSelect: (fileName: string) => void
  onImagesChange: () => void
  albumType: AlbumType
  onUpdateStatus: (fileName: string, newStatus: number) => Promise<void>
}

const GalleryAlbums: React.FC<GalleryAlbumsProps> = ({
  images,
  selectedImages,
  onImageSelect,
  onImagesChange,
  albumType,
  onUpdateStatus,
}) => {
  const { t } = useTranslation()
  const [showHideModal, setShowHideModal] = useState(false)
  const [imagesToHide, setImagesToHide] = useState<string[]>([])

  const albumImages = images.filter(image =>
    isImageInAlbum(image.status, albumType),
  )

  const handleHideImage = async (fileName: string) => {
    try {
      const image = images.find(img => img.fileName === fileName)
      if (!image) return

      const newStatus = removeFromAlbum(image.status, albumType)
      await onUpdateStatus(fileName, newStatus)
      toast.success(t('image-hidden-from-album'))
      onImagesChange()
    } catch (error) {
      console.error('Failed to hide image:', error)
      toast.error(t('failed-to-hide-image'))
    }
  }

  const handleHideSelected = async () => {
    try {
      await Promise.all(
        imagesToHide.map(fileName => {
          const image = images.find(img => img.fileName === fileName)
          if (!image) return

          const newStatus = removeFromAlbum(image.status, albumType)
          return onUpdateStatus(fileName, newStatus)
        }),
      )

      toast.success(t('images-hidden-from-album'))
      setShowHideModal(false)
      setImagesToHide([])
      onImagesChange()
    } catch (error) {
      console.error('Failed to hide images:', error)
      toast.error(t('failed-to-hide-images'))
    }
  }

  return (
    <div>
      {albumImages.length > 0 && (
        <div className='flex items-center gap-2 p-2 bg-anthracite/10 rounded-lg mb-1'>
          <FaInfoCircle className='text-redpink w-5 h-5 shrink-0' />
          <p className='text-sm text-anthracite'>{t('album-image-info')}</p>
        </div>
      )}
      {albumImages.length < 1 && (
        <div className='flex flex-col items-center justify-center p-8 bg-grisclair rounded-lg mb-0 gap-3'>
          <div className='relative'>
            <FaFolderOpen className='text-redpink w-12 h-12 opacity-80' />
            <div className='absolute -top-1 -right-1 flex items-center justify-center w-6 h-6 bg-anthracite text-white text-xs font-medium rounded-full'>
              0
            </div>
          </div>
          <div className='text-center'>
            <p className='text-base text-anthracite font-medium'>
              {t('no-images-in-album')}
            </p>
            <p className='text-sm text-anthracite/70 mt-1'>
              {t('select-images-in-main-gallery')}
            </p>
          </div>
        </div>
      )}
      <div className='grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-1'>
        {albumImages.map(image => (
          <div
            key={image.fileName}
            className={`relative group bg-white rounded-xl overflow-hidden cursor-pointer h-full ${
              selectedImages.includes(image.fileName)
                ? 'border-4 border-redpink'
                : 'border-0 border-transparent hover:border-redpink'
            }`}
            onClick={e => {
              const target = e.target as HTMLElement
              if (
                target.tagName === 'BUTTON' ||
                target.tagName === 'INPUT' ||
                target.closest('button')
              ) {
                return
              }
              onImageSelect(image.fileName)
            }}
          >
            <div className='h-full w-full aspect-square overflow-hidden rounded-lg'>
              <ImageWithApiKey imageUrl={image.url} apiKey={X_API_KEY} />
              <div className='absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-100'>
                <div
                  className={`absolute top-2 right-2 space-x-1 sm:space-x-2 flex z-10 transition-all duration-100
      ${
        selectedImages.includes(image.fileName)
          ? 'opacity-100 pointer-events-auto'
          : 'lg:opacity-0 lg:pointer-events-none'
      }
      lg:group-hover:opacity-100 lg:group-hover:pointer-events-auto
    `}
                >
                  <button
                    onClick={e => {
                      event?.preventDefault()
                      e.stopPropagation()
                      handleHideImage(image.fileName)
                    }}
                    className='p-1 sm:p-1.5 md:p-2 bg-white rounded-full hover:bg-yellow-100 transition-colors duration-100'
                    title={t('hide-from-album')}
                  >
                    <EyeOff className='h-3 w-3 sm:h-4 sm:w-4' />
                  </button>
                </div>
              </div>
            </div>
            <div className='rounded-lg'>
              <label
                className='absolute bottom-0 left-1/2 transform -translate-x-1/2 cursor-pointer z-10'
                onClick={e => e.stopPropagation()}
              >
                <input
                  type='checkbox'
                  checked={selectedImages.includes(image.fileName)}
                  onChange={e => {
                    e.stopPropagation()
                    onImageSelect(image.fileName)
                  }}
                  className={`
                    appearance-none
                    h-4 
                    w-4 
                    rounded 
                    border-2
                    transition-all 
                    duration-100 
                    cursor-pointer 
                    ${
                      selectedImages.includes(image.fileName)
                        ? 'bg-[#FF1654] border-[#FF1654]'
                        : 'border-gray-300 bg-white'
                    }
                  `}
                />
              </label>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showHideModal} onClose={() => setShowHideModal(false)}>
        <div className='fixed inset-0 bg-black/30' aria-hidden='true' />
        <div className='fixed inset-0 flex items-center justify-center p-4'>
          <Dialog.Panel className='bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl'>
            <Dialog.Title className='text-lg font-medium text-gray-900'>
              {t('confirm-hide-from-album')}
            </Dialog.Title>
            <div className='mt-2'>
              <p className='text-sm text-gray-500'>
                {t('hide-images-warning', { count: imagesToHide.length })}
              </p>
            </div>
            <div className='mt-4 flex justify-end space-x-2'>
              <button
                type='button'
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50'
                onClick={() => setShowHideModal(false)}
              >
                {t('cancel')}
              </button>
              <button
                type='button'
                className='px-4 py-2 text-sm font-medium text-white bg-redpink rounded-md hover:bg-red-700'
                onClick={handleHideSelected}
              >
                {t('hide')}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  )
}

export default GalleryAlbums
